/**
 * Projection from visual design analysis output into Theme V2-safe patches.
 *
 * The projector is deliberately conservative:
 * - low-confidence tokens are skipped with warnings
 * - numeric geometry is range-checked
 * - only renderer-backed nested paths are written outside layoutSettings
 */

import type {
  VisualDesignAnalysis,
  VisualDesignBackdropToken,
  VisualDesignButtonGeometryTokens,
  VisualDesignOptionsListGeometryTokens,
  VisualDesignValueToken,
} from '@modules/shared-types';
import type {
  Backdrop,
  EdgeInsets,
  FontFamily,
  TypographyStyle,
} from '@segmently/schema-models/v2';
import type {
  ThemeV2,
  ThemeV2LayoutSettings,
  GeneratedThemeV2Metadata,
} from '@modules/themes/types/themesV2.types';
// Backend-reachable file (imported by builder-service screenReproduction):
// VALUE imports must use RELATIVE paths — alias value imports become runtime
// alias requires in the builder dist and trip deploy Guard 4.
import { setValueByPath } from '../mappers/objectPath';

export type ThemeV2ProjectionWarningCode =
  | 'low-confidence'
  | 'invalid-color'
  | 'invalid-number'
  | 'invalid-insets'
  | 'invalid-typography'
  | 'unsupported-token';

export interface ThemeV2ProjectionWarning {
  code: ThemeV2ProjectionWarningCode;
  sourcePath: string;
  message: string;
  confidence?: number;
}

export interface ThemeV2ProjectionOptions {
  /** Minimum confidence required for a token to be projected. */
  minConfidence?: number;
  /** Attach generatedMetadata to the patch. Defaults to true. */
  includeGeneratedMetadata?: boolean;
}

type Primitive = string | number | boolean | null | undefined;
type DeepPartial<T> = T extends Primitive
  ? T
  : T extends Array<infer U>
    ? Array<DeepPartial<U>>
    : { [K in keyof T]?: DeepPartial<T[K]> };

export type ThemeV2ProjectionPatch = DeepPartial<Pick<
  ThemeV2,
  | 'colors'
  | 'background'
  | 'fonts'
  | 'optionsListSettings'
  | 'layoutSettings'
  | 'actionBar'
  | 'generatedMetadata'
>>;

export interface ThemeV2ProjectionResult {
  patch: ThemeV2ProjectionPatch;
  warnings: ThemeV2ProjectionWarning[];
  projectedPaths: string[];
}

interface NumberRange {
  min: number;
  max: number;
}

const DEFAULT_MIN_CONFIDENCE = 0.72;

const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{6}|[0-9a-f]{8})$/i;
const SUPPORTED_ALIGNMENTS = new Set(['start', 'center', 'end']);
const SUPPORTED_FONT_FAMILIES = new Set(['SF Pro', 'SF ProRounded', 'SF Mono', 'New York']);

export const THEME_V2_IMAGE_TOKEN_PROJECTION_TARGETS = [
  'colors.buttonsBackground',
  'colors.titlesSubtitles',
  'colors.buttonsTitle',
  'background',
  'fonts.main.fontFamily',
  'fonts.title',
  'fonts.subtitle',
  'optionsListSettings.itemTitle',
  'optionsListSettings.itemSubtitle',
  'layoutSettings.spacingScale',
  'layoutSettings.radiusScale',
  'layoutSettings.actionBar.insets',
  'layoutSettings.actionBar.buttonGap',
  'layoutSettings.actionBar.primaryButton.radius',
  'layoutSettings.actionBar.primaryButton.height',
  'layoutSettings.actionBar.primaryButton.insets',
  'layoutSettings.actionBar.secondaryButton.radius',
  'layoutSettings.actionBar.secondaryButton.height',
  'layoutSettings.actionBar.secondaryButton.insets',
  'layoutSettings.optionsList.itemRadius',
  'layoutSettings.optionsList.itemInsets',
  'layoutSettings.optionsList.layoutInsets',
  'layoutSettings.optionsList.itemsSpacing',
  'layoutSettings.optionsList.cellHeightFromCellWidth',
  'layoutSettings.optionsList.useMaxCellHeight',
  'layoutSettings.media.featuredHeightPercentage',
  'layoutSettings.media.cornerRadius',
  'layoutSettings.form.fieldHeight',
  'layoutSettings.form.fieldRadius',
  'layoutSettings.form.fieldInsets',
  'layoutSettings.picker.containerRadius',
  'layoutSettings.picker.containerInsets',
  'layoutSettings.progress.indicatorHeight',
  'layoutSettings.progress.trackHeight',
  'layoutSettings.progress.containerInsets',
  'layoutSettings.paywall.productRadius',
  'layoutSettings.paywall.productInsets',
  'layoutSettings.paywall.productSpacing',
  'layoutSettings.paywall.footerInsets',
  'layoutSettings.webEmbed.contentInsets',
  'layoutSettings.flexibleLayout.containerInsets',
  'layoutSettings.flexibleLayout.sectionPadding',
  'layoutSettings.flexibleLayout.sectionRadius',
  'layoutSettings.flexibleLayout.sectionSpacing',
  'actionBar.appearance.insets',
  'actionBar.primary.appearance.borders.radius.uniform',
  'actionBar.primary.appearance.dimensions.height',
  'actionBar.primary.appearance.insets',
  'actionBar.secondary.appearance.borders.radius.uniform',
  'actionBar.secondary.appearance.dimensions.height',
  'actionBar.secondary.appearance.insets',
] as const;

function shouldUseToken<T>(
  token: VisualDesignValueToken<T> | undefined,
  sourcePath: string,
  warnings: ThemeV2ProjectionWarning[],
  minConfidence: number
): token is VisualDesignValueToken<T> {
  if (!token) return false;
  if (token.confidence !== undefined && token.confidence < minConfidence) {
    warnings.push({
      code: 'low-confidence',
      sourcePath,
      confidence: token.confidence,
      message: `Skipped ${sourcePath}: confidence ${token.confidence} is below ${minConfidence}.`,
    });
    return false;
  }
  return true;
}

function setProjectedValue(
  patch: ThemeV2ProjectionPatch,
  projectedPaths: string[],
  path: string,
  value: unknown
): void {
  setValueByPath(patch as Record<string, unknown>, path, value);
  projectedPaths.push(path);
}

function readColorToken(
  token: VisualDesignValueToken<string> | undefined,
  sourcePath: string,
  warnings: ThemeV2ProjectionWarning[],
  minConfidence: number
): string | undefined {
  if (!shouldUseToken(token, sourcePath, warnings, minConfidence)) return undefined;
  const value = token.value.trim();
  if (!HEX_COLOR_PATTERN.test(value)) {
    warnings.push({
      code: 'invalid-color',
      sourcePath,
      message: `Skipped ${sourcePath}: expected #RRGGBB or #RRGGBBAA.`,
      confidence: token.confidence,
    });
    return undefined;
  }
  return value;
}

/**
 * Project a backdrop token into a ThemeV2 `Backdrop`. v1 supports only `kind: 'solid'`;
 * any other kind (gradient/photo) is skipped with an `unsupported-token` warning rather
 * than guessing a `content` shape we cannot render.
 */
function readBackdropToken(
  token: VisualDesignValueToken<VisualDesignBackdropToken> | undefined,
  sourcePath: string,
  warnings: ThemeV2ProjectionWarning[],
  minConfidence: number
): Backdrop | undefined {
  if (!shouldUseToken(token, sourcePath, warnings, minConfidence)) return undefined;
  const value = token.value;
  if (value.kind !== 'solid') {
    warnings.push({
      code: 'unsupported-token',
      sourcePath,
      message: `Skipped ${sourcePath}: only solid backdrops are projected (got kind="${value.kind}").`,
      confidence: token.confidence,
    });
    return undefined;
  }
  const color = value.color?.trim();
  if (!color || !HEX_COLOR_PATTERN.test(color)) {
    warnings.push({
      code: 'invalid-color',
      sourcePath: `${sourcePath}.color`,
      message: `Skipped ${sourcePath}: solid backdrop color must be #RRGGBB or #RRGGBBAA.`,
      confidence: token.confidence,
    });
    return undefined;
  }
  return { kind: 'solid', content: { color } };
}

function readNumberToken(
  token: VisualDesignValueToken<number> | undefined,
  sourcePath: string,
  range: NumberRange,
  warnings: ThemeV2ProjectionWarning[],
  minConfidence: number
): number | undefined {
  if (!shouldUseToken(token, sourcePath, warnings, minConfidence)) return undefined;
  const value = token.value;
  if (!Number.isFinite(value) || value < range.min || value > range.max) {
    warnings.push({
      code: 'invalid-number',
      sourcePath,
      message: `Skipped ${sourcePath}: expected a finite number between ${range.min} and ${range.max}.`,
      confidence: token.confidence,
    });
    return undefined;
  }
  return value;
}

function readInsetsToken(
  token: VisualDesignValueToken<EdgeInsets> | undefined,
  sourcePath: string,
  warnings: ThemeV2ProjectionWarning[],
  minConfidence: number
): EdgeInsets | undefined {
  if (!shouldUseToken(token, sourcePath, warnings, minConfidence)) return undefined;
  const result: EdgeInsets = {};
  for (const key of ['top', 'bottom', 'start', 'end'] as const) {
    const value = token.value[key];
    if (value === undefined) continue;
    if (!Number.isFinite(value) || value < 0 || value > 256) {
      warnings.push({
        code: 'invalid-insets',
        sourcePath: `${sourcePath}.${key}`,
        message: `Skipped ${sourcePath}: inset ${key} must be between 0 and 256.`,
        confidence: token.confidence,
      });
      return undefined;
    }
    result[key] = value;
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

function readTypographyToken(
  token: VisualDesignValueToken<Partial<TypographyStyle>> | undefined,
  sourcePath: string,
  warnings: ThemeV2ProjectionWarning[],
  minConfidence: number
): Partial<TypographyStyle> | undefined {
  if (!shouldUseToken(token, sourcePath, warnings, minConfidence)) return undefined;

  const value = token.value;
  const result: Partial<TypographyStyle> = {};

  if (value.fontFamily !== undefined) {
    if (!SUPPORTED_FONT_FAMILIES.has(value.fontFamily)) {
      warnings.push({
        code: 'invalid-typography',
        sourcePath: `${sourcePath}.fontFamily`,
        message: `Skipped ${sourcePath}: fontFamily is not supported by Theme V2.`,
        confidence: token.confidence,
      });
      return undefined;
    }
    result.fontFamily = value.fontFamily;
  }

  if (value.fontSize !== undefined) {
    if (!Number.isFinite(value.fontSize) || value.fontSize < 1 || value.fontSize > 200) {
      warnings.push({
        code: 'invalid-typography',
        sourcePath: `${sourcePath}.fontSize`,
        message: `Skipped ${sourcePath}: fontSize must be between 1 and 200.`,
        confidence: token.confidence,
      });
      return undefined;
    }
    result.fontSize = value.fontSize;
  }

  if (value.fontWeight !== undefined) {
    if (!Number.isFinite(value.fontWeight) || value.fontWeight < 100 || value.fontWeight > 1000) {
      warnings.push({
        code: 'invalid-typography',
        sourcePath: `${sourcePath}.fontWeight`,
        message: `Skipped ${sourcePath}: fontWeight must be between 100 and 1000.`,
        confidence: token.confidence,
      });
      return undefined;
    }
    result.fontWeight = value.fontWeight;
  }

  if (value.lineHeight !== undefined) {
    if (!Number.isFinite(value.lineHeight) || value.lineHeight < 0.5 || value.lineHeight > 3) {
      warnings.push({
        code: 'invalid-typography',
        sourcePath: `${sourcePath}.lineHeight`,
        message: `Skipped ${sourcePath}: lineHeight must be between 0.5 and 3.`,
        confidence: token.confidence,
      });
      return undefined;
    }
    result.lineHeight = value.lineHeight;
  }

  if (value.align !== undefined) {
    if (!SUPPORTED_ALIGNMENTS.has(value.align)) {
      warnings.push({
        code: 'invalid-typography',
        sourcePath: `${sourcePath}.align`,
        message: `Skipped ${sourcePath}: align must be start, center, or end.`,
        confidence: token.confidence,
      });
      return undefined;
    }
    result.align = value.align;
  }

  if (value.color !== undefined) {
    const color = readColorToken(
      { value: value.color, confidence: token.confidence },
      `${sourcePath}.color`,
      warnings,
      minConfidence
    );
    if (!color) return undefined;
    result.color = color;
  }

  if (value.insets !== undefined) {
    const insets = readInsetsToken(
      { value: value.insets, confidence: token.confidence },
      `${sourcePath}.insets`,
      warnings,
      minConfidence
    );
    if (!insets) return undefined;
    result.insets = insets;
  }

  return Object.keys(result).length > 0 ? result : undefined;
}

function readNumberRecordToken(
  token: VisualDesignValueToken<Record<string, number>> | undefined,
  sourcePath: string,
  warnings: ThemeV2ProjectionWarning[],
  minConfidence: number
): Record<string, number> | undefined {
  if (!shouldUseToken(token, sourcePath, warnings, minConfidence)) return undefined;
  const result: Record<string, number> = {};
  for (const [key, value] of Object.entries(token.value)) {
    if (!Number.isFinite(value) || value < 0 || value > 9999) {
      warnings.push({
        code: 'invalid-number',
        sourcePath: `${sourcePath}.${key}`,
        message: `Skipped ${sourcePath}: all scale values must be finite non-negative numbers.`,
        confidence: token.confidence,
      });
      return undefined;
    }
    result[key] = value;
  }
  return result;
}

function readFontFamilyToken(
  token: VisualDesignValueToken<string> | undefined,
  sourcePath: string,
  warnings: ThemeV2ProjectionWarning[],
  minConfidence: number
): FontFamily | undefined {
  if (!shouldUseToken(token, sourcePath, warnings, minConfidence)) return undefined;
  if (!SUPPORTED_FONT_FAMILIES.has(token.value)) {
    warnings.push({
      code: 'invalid-typography',
      sourcePath,
      message: `Skipped ${sourcePath}: fontFamily is not supported by Theme V2.`,
      confidence: token.confidence,
    });
    return undefined;
  }
  return token.value as FontFamily;
}

function addGeneratedMetadata(
  patch: ThemeV2ProjectionPatch,
  analysis: VisualDesignAnalysis,
  warnings: ThemeV2ProjectionWarning[]
): void {
  const metadata: GeneratedThemeV2Metadata = {
    sourceKind: 'image-analysis',
    analysisId: analysis.id,
    generatedAt: new Date().toISOString(),
    warnings: warnings.map((warning) => `${warning.sourcePath}: ${warning.message}`),
  };
  // Type-level cast only: DeepPartial maps `Record<string, unknown>` index
  // signatures (measurementBasis) to `{} | undefined`, which `unknown` cannot
  // satisfy under tsc strict programs (surfaced by builder-service inclusion).
  patch.generatedMetadata = metadata as ThemeV2ProjectionPatch['generatedMetadata'];
}

function projectButtonGeometry(
  button: VisualDesignButtonGeometryTokens | undefined,
  sourcePrefix: string,
  layoutPrefix: string,
  actionBarPrefix: string,
  patch: ThemeV2ProjectionPatch,
  projectedPaths: string[],
  warnings: ThemeV2ProjectionWarning[],
  minConfidence: number
): void {
  const radius = readNumberToken(
    button?.radius,
    `${sourcePrefix}.radius`,
    { min: 0, max: 128 },
    warnings,
    minConfidence
  );
  if (radius !== undefined) {
    setProjectedValue(patch, projectedPaths, `${layoutPrefix}.radius`, radius);
    setProjectedValue(patch, projectedPaths, `${actionBarPrefix}.appearance.borders.radius.uniform`, radius);
  }

  const height = readNumberToken(
    button?.height,
    `${sourcePrefix}.height`,
    { min: 16, max: 160 },
    warnings,
    minConfidence
  );
  if (height !== undefined) {
    setProjectedValue(patch, projectedPaths, `${layoutPrefix}.height`, height);
    setProjectedValue(patch, projectedPaths, `${actionBarPrefix}.appearance.dimensions.height`, height);
  }

  const insets = readInsetsToken(
    button?.insets,
    `${sourcePrefix}.insets`,
    warnings,
    minConfidence
  );
  if (insets) {
    setProjectedValue(patch, projectedPaths, `${layoutPrefix}.insets`, insets);
    setProjectedValue(patch, projectedPaths, `${actionBarPrefix}.appearance.insets`, insets);
  }
}

/**
 * Project visual design analysis into a ThemeV2-compatible patch.
 */
export function projectVisualAnalysisToThemeV2(
  analysis: VisualDesignAnalysis,
  options: ThemeV2ProjectionOptions = {}
): ThemeV2ProjectionResult {
  const minConfidence = options.minConfidence ?? DEFAULT_MIN_CONFIDENCE;
  const patch: ThemeV2ProjectionPatch = {};
  const warnings: ThemeV2ProjectionWarning[] = [];
  const projectedPaths: string[] = [];

  const colors = analysis.tokens.colors;
  const colorTargets = {
    buttonsBackground: colors?.buttonsBackground,
    titlesSubtitles: colors?.titlesSubtitles,
    buttonsTitle: colors?.buttonsTitle,
  };
  for (const [key, token] of Object.entries(colorTargets)) {
    const color = readColorToken(token, `tokens.colors.${key}`, warnings, minConfidence);
    if (color) {
      setProjectedValue(patch, projectedPaths, `colors.${key}`, color);
    }
  }

  const backdrop = readBackdropToken(
    analysis.tokens.background,
    'tokens.background',
    warnings,
    minConfidence
  );
  if (backdrop) {
    setProjectedValue(patch, projectedPaths, 'background', backdrop);
  }

  const typography = analysis.tokens.typography;
  const mainFontFamily = readFontFamilyToken(
    typography?.mainFontFamily,
    'tokens.typography.mainFontFamily',
    warnings,
    minConfidence
  );
  if (mainFontFamily) {
    setProjectedValue(
      patch,
      projectedPaths,
      'fonts.main.fontFamily',
      mainFontFamily
    );
  }

  const typographyTargets: Array<[
    keyof NonNullable<typeof typography>,
    string,
  ]> = [
    ['title', 'fonts.title'],
    ['subtitle', 'fonts.subtitle'],
    ['optionTitle', 'optionsListSettings.itemTitle'],
    ['optionSubtitle', 'optionsListSettings.itemSubtitle'],
  ];
  for (const [key, targetPath] of typographyTargets) {
    const token = typography?.[key];
    const value = readTypographyToken(
      token as VisualDesignValueToken<Partial<TypographyStyle>> | undefined,
      `tokens.typography.${key}`,
      warnings,
      minConfidence
    );
    if (value) {
      setProjectedValue(patch, projectedPaths, targetPath, value);
    }
  }

  const geometry = analysis.tokens.geometry;
  const spacingScale = readNumberRecordToken(
    geometry?.spacingScale,
    'tokens.geometry.spacingScale',
    warnings,
    minConfidence
  );
  if (spacingScale) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.spacingScale', spacingScale);
  }

  const radiusScale = readNumberRecordToken(
    geometry?.radiusScale,
    'tokens.geometry.radiusScale',
    warnings,
    minConfidence
  );
  if (radiusScale) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.radiusScale', radiusScale);
  }

  const actionBarInsets = readInsetsToken(
    geometry?.actionBar?.insets,
    'tokens.geometry.actionBar.insets',
    warnings,
    minConfidence
  );
  if (actionBarInsets) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.actionBar.insets', actionBarInsets);
    setProjectedValue(patch, projectedPaths, 'actionBar.appearance.insets', actionBarInsets);
  }

  const buttonGap = readNumberToken(
    geometry?.actionBar?.buttonGap,
    'tokens.geometry.actionBar.buttonGap',
    { min: 0, max: 128 },
    warnings,
    minConfidence
  );
  if (buttonGap !== undefined) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.actionBar.buttonGap', buttonGap);
  }

  projectButtonGeometry(
    geometry?.actionBar?.primaryButton,
    'tokens.geometry.actionBar.primaryButton',
    'layoutSettings.actionBar.primaryButton',
    'actionBar.primary',
    patch,
    projectedPaths,
    warnings,
    minConfidence
  );

  projectButtonGeometry(
    geometry?.actionBar?.secondaryButton,
    'tokens.geometry.actionBar.secondaryButton',
    'layoutSettings.actionBar.secondaryButton',
    'actionBar.secondary',
    patch,
    projectedPaths,
    warnings,
    minConfidence
  );

  const optionGeometryMap: Array<[
    keyof VisualDesignOptionsListGeometryTokens,
    string,
    NumberRange,
  ]> = [
    ['itemRadius', 'layoutSettings.optionsList.itemRadius', { min: 0, max: 128 }],
    ['itemsSpacing', 'layoutSettings.optionsList.itemsSpacing', { min: 0, max: 128 }],
    ['cellHeightFromCellWidth', 'layoutSettings.optionsList.cellHeightFromCellWidth', { min: 20, max: 300 }],
  ];
  for (const [key, targetPath, range] of optionGeometryMap) {
    const value = readNumberToken(
      geometry?.optionsList?.[key] as VisualDesignValueToken<number> | undefined,
      `tokens.geometry.optionsList.${key}`,
      range,
      warnings,
      minConfidence
    );
    if (value !== undefined) {
      setProjectedValue(patch, projectedPaths, targetPath, value);
    }
  }

  const optionItemInsets = readInsetsToken(
    geometry?.optionsList?.itemInsets,
    'tokens.geometry.optionsList.itemInsets',
    warnings,
    minConfidence
  );
  if (optionItemInsets) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.optionsList.itemInsets', optionItemInsets);
  }

  const optionLayoutInsets = readInsetsToken(
    geometry?.optionsList?.layoutInsets,
    'tokens.geometry.optionsList.layoutInsets',
    warnings,
    minConfidence
  );
  if (optionLayoutInsets) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.optionsList.layoutInsets', optionLayoutInsets);
  }

  if (geometry?.optionsList?.useMaxCellHeight && shouldUseToken(
    geometry.optionsList.useMaxCellHeight,
    'tokens.geometry.optionsList.useMaxCellHeight',
    warnings,
    minConfidence
  )) {
    setProjectedValue(
      patch,
      projectedPaths,
      'layoutSettings.optionsList.useMaxCellHeight',
      geometry.optionsList.useMaxCellHeight.value
    );
  }

  const mediaHeight = readNumberToken(
    geometry?.media?.featuredHeightPercentage,
    'tokens.geometry.media.featuredHeightPercentage',
    { min: 1, max: 100 },
    warnings,
    minConfidence
  );
  if (mediaHeight !== undefined) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.media.featuredHeightPercentage', mediaHeight);
  }

  const mediaRadius = readNumberToken(
    geometry?.media?.cornerRadius,
    'tokens.geometry.media.cornerRadius',
    { min: 0, max: 128 },
    warnings,
    minConfidence
  );
  if (mediaRadius !== undefined) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.media.cornerRadius', mediaRadius);
  }

  const formHeight = readNumberToken(
    geometry?.form?.fieldHeight,
    'tokens.geometry.form.fieldHeight',
    { min: 16, max: 160 },
    warnings,
    minConfidence
  );
  if (formHeight !== undefined) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.form.fieldHeight', formHeight);
  }

  const formRadius = readNumberToken(
    geometry?.form?.fieldRadius,
    'tokens.geometry.form.fieldRadius',
    { min: 0, max: 128 },
    warnings,
    minConfidence
  );
  if (formRadius !== undefined) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.form.fieldRadius', formRadius);
  }

  const formInsets = readInsetsToken(
    geometry?.form?.fieldInsets,
    'tokens.geometry.form.fieldInsets',
    warnings,
    minConfidence
  );
  if (formInsets) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.form.fieldInsets', formInsets);
  }

  const pickerRadius = readNumberToken(
    geometry?.picker?.containerRadius,
    'tokens.geometry.picker.containerRadius',
    { min: 0, max: 128 },
    warnings,
    minConfidence
  );
  if (pickerRadius !== undefined) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.picker.containerRadius', pickerRadius);
  }

  const pickerInsets = readInsetsToken(
    geometry?.picker?.containerInsets,
    'tokens.geometry.picker.containerInsets',
    warnings,
    minConfidence
  );
  if (pickerInsets) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.picker.containerInsets', pickerInsets);
  }

  const progressIndicatorHeight = readNumberToken(
    geometry?.progress?.indicatorHeight,
    'tokens.geometry.progress.indicatorHeight',
    { min: 1, max: 128 },
    warnings,
    minConfidence
  );
  if (progressIndicatorHeight !== undefined) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.progress.indicatorHeight', progressIndicatorHeight);
  }

  const progressTrackHeight = readNumberToken(
    geometry?.progress?.trackHeight,
    'tokens.geometry.progress.trackHeight',
    { min: 1, max: 128 },
    warnings,
    minConfidence
  );
  if (progressTrackHeight !== undefined) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.progress.trackHeight', progressTrackHeight);
  }

  const progressInsets = readInsetsToken(
    geometry?.progress?.containerInsets,
    'tokens.geometry.progress.containerInsets',
    warnings,
    minConfidence
  );
  if (progressInsets) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.progress.containerInsets', progressInsets);
  }

  const paywallRadius = readNumberToken(
    geometry?.paywall?.productRadius,
    'tokens.geometry.paywall.productRadius',
    { min: 0, max: 128 },
    warnings,
    minConfidence
  );
  if (paywallRadius !== undefined) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.paywall.productRadius', paywallRadius);
  }

  const paywallProductSpacing = readNumberToken(
    geometry?.paywall?.productSpacing,
    'tokens.geometry.paywall.productSpacing',
    { min: 0, max: 128 },
    warnings,
    minConfidence
  );
  if (paywallProductSpacing !== undefined) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.paywall.productSpacing', paywallProductSpacing);
  }

  const paywallProductInsets = readInsetsToken(
    geometry?.paywall?.productInsets,
    'tokens.geometry.paywall.productInsets',
    warnings,
    minConfidence
  );
  if (paywallProductInsets) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.paywall.productInsets', paywallProductInsets);
  }

  const paywallFooterInsets = readInsetsToken(
    geometry?.paywall?.footerInsets,
    'tokens.geometry.paywall.footerInsets',
    warnings,
    minConfidence
  );
  if (paywallFooterInsets) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.paywall.footerInsets', paywallFooterInsets);
  }

  const webEmbedInsets = readInsetsToken(
    geometry?.webEmbed?.contentInsets,
    'tokens.geometry.webEmbed.contentInsets',
    warnings,
    minConfidence
  );
  if (webEmbedInsets) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.webEmbed.contentInsets', webEmbedInsets);
  }

  const flexibleContainerInsets = readInsetsToken(
    geometry?.flexibleLayout?.containerInsets,
    'tokens.geometry.flexibleLayout.containerInsets',
    warnings,
    minConfidence
  );
  if (flexibleContainerInsets) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.flexibleLayout.containerInsets', flexibleContainerInsets);
  }

  const flexibleSectionPadding = readInsetsToken(
    geometry?.flexibleLayout?.sectionPadding,
    'tokens.geometry.flexibleLayout.sectionPadding',
    warnings,
    minConfidence
  );
  if (flexibleSectionPadding) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.flexibleLayout.sectionPadding', flexibleSectionPadding);
  }

  const flexibleSectionRadius = readNumberToken(
    geometry?.flexibleLayout?.sectionRadius,
    'tokens.geometry.flexibleLayout.sectionRadius',
    { min: 0, max: 128 },
    warnings,
    minConfidence
  );
  if (flexibleSectionRadius !== undefined) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.flexibleLayout.sectionRadius', flexibleSectionRadius);
  }

  const flexibleSectionSpacing = readNumberToken(
    geometry?.flexibleLayout?.sectionSpacing,
    'tokens.geometry.flexibleLayout.sectionSpacing',
    { min: 0, max: 128 },
    warnings,
    minConfidence
  );
  if (flexibleSectionSpacing !== undefined) {
    setProjectedValue(patch, projectedPaths, 'layoutSettings.flexibleLayout.sectionSpacing', flexibleSectionSpacing);
  }

  if (options.includeGeneratedMetadata !== false) {
    addGeneratedMetadata(patch, analysis, warnings);
  }

  return {
    patch,
    warnings,
    projectedPaths,
  };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function mergeDeepUnknown(base: unknown, patch: unknown): unknown {
  const result = Array.isArray(base)
    ? [...base]
    : isPlainObject(base)
      ? { ...base }
      : base;

  if (!isPlainObject(result) || !isPlainObject(patch)) {
    return patch;
  }

  for (const [key, patchValue] of Object.entries(patch)) {
    const baseValue = (result as Record<string, unknown>)[key];
    (result as Record<string, unknown>)[key] = isPlainObject(baseValue) && isPlainObject(patchValue)
      ? mergeDeepUnknown(baseValue, patchValue)
      : patchValue;
  }

  return result;
}

function mergeDeep<T>(base: T, patch: DeepPartial<T>): T {
  return mergeDeepUnknown(base, patch) as T;
}

/**
 * Apply a projection patch without replacing nested ThemeV2 maps wholesale.
 */
export function applyThemeV2ProjectionPatch<T extends Pick<ThemeV2, keyof ThemeV2ProjectionPatch>>(
  baseTheme: T,
  patch: ThemeV2ProjectionPatch
): T {
  return mergeDeep(baseTheme, patch as DeepPartial<T>);
}

export function extractThemeV2LayoutSettingsPatch(
  result: ThemeV2ProjectionResult
): ThemeV2LayoutSettings | undefined {
  return result.patch.layoutSettings as ThemeV2LayoutSettings | undefined;
}
